import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import globalToast from "../components/common/globalToast";

dayjs.extend(customParseFormat);

const API_DATE_FORMATS = [
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY HH:mm",
    "DD/MM/YYYY",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD",
];

const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).trim();
    for (const format of API_DATE_FORMATS) {
        const parsed = dayjs(str, format, true);
        if (parsed.isValid()) return parsed;
    }
    const fallback = dayjs(str);
    return fallback.isValid() ? fallback : null;
};

export const isOverdueRow = (row) => {
    if (!row) return false;
    const flag = row.is_overdue;
    return flag === true || flag === 1 || flag === "1";
};

export const reaisToCents = (value) => {
    if (value === undefined || value === null || value === '') {
        return 0;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
    }

    const normalized = String(value)
        .replace(/R\$\s?/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim();

    const asNumber = parseFloat(normalized);
    return Number.isFinite(asNumber) ? Math.round(asNumber * 100) : 0;
};

export const parseMoneyToCents = (value) => {
    if (value === undefined || value === null || value === '') {
        return 0;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.round(value) : 0;
    }

    const text = String(value).trim();
    if (text.includes('R$') || text.includes(',')) {
        return reaisToCents(text);
    }

    const asNumber = Number(text);
    return Number.isFinite(asNumber) ? Math.round(asNumber) : 0;
};

export const centsToReais = (cents) => (parseMoneyToCents(cents) / 100);

export const mapExtraServices = (services) =>
    (services || []).map((obj) => ({
        id: obj.id,
        value: obj.id,
        label: obj.name,
        name: obj.name,
        price: parseMoneyToCents(obj.price),
    }));

export const formatWeight = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return '0 g';
    }

    return `${new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(n)} g`;
};

// Formata um valor em centavos para o formato monetário brasileiro com máscara (ex: R$ 1.234,56)
export const moneyMask = (value = '9999') => {
    if (value === undefined || value === null) {
        return 'R$ 0,00';
    }

    value = String(value)
        .replace(/\D/g, '');

    if (value === '') value = '0';

    const options = { minimumFractionDigits: 2 };
    const result = new Intl.NumberFormat('pt-BR', options).format(
        parseFloat(value) / 100
    );

    return 'R$ ' + result;
};

// Formata centavos sem o símbolo (ex: 1.234,56). Usado na cotação do header.
export const moneyFormat = (value = '9999') => {

    if (value === undefined || value === null) {
        return '0,00';
    };

    value = String(value)?.replace('.', '')?.replace(',', '')?.replace(/\D/g, '');

    const options = { minimumFractionDigits: 2 }
    const result = new Intl.NumberFormat('pt-BR', options).format(
        parseFloat(value) / 100
    );

    return result;
};

// Formata centavos para moeda brasileira (R$)
export const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') {
        return 'R$ 0,00';
    }

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(parseMoneyToCents(value) / 100);
};

// Copia um texto para a área de transferência e exibe um toast de sucesso
export const copyToClipBoard = async (text, title = 'Texto') => {

    if (text) {
        await navigator.clipboard.writeText(text);
        globalToast.success(`${title} copiado para sua área de transferência!`);
    };

};

// Remove todos os caracteres não numéricos de uma string
export const clearNonNumericCharacters = (char) => {

    return char?.replace(/\D/g, '');
};

// Retorna a URL da imagem se existir, caso contrário retorna uma imagem padrão
export const hasImage = (img) => {

    return img
        ? img
        : "https://inovartgold.com.br/assets/logo/logo.jpg";
};

// Formata dados vindos da API para o formato de opções (value/label)
export const formatOptionsFromApi = (data) =>
    data?.data?.data?.data?.map(item => ({ value: item.id, label: item.name })) || [];

// Formata datas no padrão brasileiro (DD/MM/YYYY HH:mm)
export const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const date = parseApiDate(dateStr);
    if (!date) return dateStr;
    return date.format('DD/MM/YYYY HH:mm');
};

export const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = parseApiDate(dateStr);
    if (!date) return dateStr;
    return date.format('DD/MM/YYYY');
};

export const formatInteger = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '0';
    return number.toLocaleString('pt-BR');
};

export const formatPercent = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    if (Number.isNaN(number)) return null;
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: Number.isInteger(number) ? 0 : 1,
        maximumFractionDigits: 1,
    });
    return `${number > 0 ? '+' : ''}${formatted}%`;
};

export const formatDays = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '0 dias';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: Number.isInteger(number) ? 0 : 1,
        maximumFractionDigits: 1,
    });
    return `${formatted} ${number === 1 ? 'dia' : 'dias'}`;
};

// Verifica se o código está rodando em um ambiente Electron
export function isElectron() {
    return !!(window && window.process && window.process.type);
  }

export function confirmAndExecuteFn(message, actionFn) {
    if (window.confirm(message)) {
        actionFn();
    }
};